import { z } from 'zod';
import { STORE_CATEGORIES, INGREDIENT_SOURCES } from '../catalog.ts';
import { NUTRIENT_KEYS, type NutrientKey } from '../nutrients.ts';
import { unitSchema } from './units.ts';

const price = z.number().nonnegative().max(10_000).nullable();

export const nutrientsSchema = z.object(
  Object.fromEntries(NUTRIENT_KEYS.map((k) => [k, z.number().nonnegative().max(1000)])) as Record<
    NutrientKey,
    z.ZodNumber
  >,
);

export const unitGramsSchema = z.partialRecord(unitSchema, z.number().positive().max(100_000));
export const packSizeSchema = z.object({ qty: z.number().positive(), unit: unitSchema });

/** Catalog fields a user can set (the fix sheet, P1.16). Saving marks the entry verified. */
export const ingredientUpdateSchema = z
  .object({
    nameEn: z.string().trim().min(1).max(120),
    nameDe: z.string().trim().min(1).max(120),
    aliases: z.array(z.string().trim().toLowerCase().min(1).max(120)).max(40),
    category: z.enum(STORE_CATEGORIES),
    fdcId: z.number().int().positive().nullable(),
    nutrients: nutrientsSchema.nullable(),
    unitGrams: unitGramsSchema,
    densityGPerMl: z.number().positive().max(5).nullable(),
    packSize: packSizeSchema.nullable(),
    priceEurPerKg: price,
    priceEurPerPiece: price,
    shelfDays: z.number().int().min(0).max(3650).nullable(),
  })
  .partial();
export type IngredientUpdate = z.infer<typeof ingredientUpdateSchema>;

export const ingredientCreateSchema = ingredientUpdateSchema.required({
  nameEn: true,
  nameDe: true,
});
export type IngredientCreate = z.infer<typeof ingredientCreateSchema>;

/** This household's overrides. */
export const householdIngredientUpdateSchema = z
  .object({
    priceEurPerKg: price,
    priceEurPerPiece: price,
    /** Null = the catalog default. */
    isStaple: z.boolean().nullable(),
    runningLow: z.boolean(),
  })
  .partial();
export type HouseholdIngredientUpdate = z.infer<typeof householdIngredientUpdateSchema>;

export const catalogIngredientSchema = z.object({
  id: z.uuid(),
  nameEn: z.string(),
  nameDe: z.string(),
  aliases: z.array(z.string()),
  category: z.enum(STORE_CATEGORIES),
  fdcId: z.number().int().nullable(),
  nutrients: nutrientsSchema.nullable(),
  unitGrams: unitGramsSchema,
  densityGPerMl: z.number().nullable(),
  packSize: packSizeSchema.nullable(),
  priceEurPerKg: z.number().nullable(),
  priceEurPerPiece: z.number().nullable(),
  shelfDays: z.number().int().nullable(),
  defaultStaple: z.boolean(),
  verified: z.boolean(),
  source: z.enum(INGREDIENT_SOURCES),
  /** The household's effective view (overrides applied). */
  household: z.object({
    priceEurPerKg: z.number().nullable(),
    priceEurPerPiece: z.number().nullable(),
    priceOverridden: z.boolean(),
    isStaple: z.boolean(),
    runningLow: z.boolean(),
  }),
});
export type CatalogIngredient = z.infer<typeof catalogIngredientSchema>;
export const catalogListSchema = z.object({ ingredients: z.array(catalogIngredientSchema) });

/** An FDC food offered in the fix sheet. */
export const fdcFoodSchema = z.object({
  fdcId: z.number().int(),
  description: z.string(),
  category: z.string().nullable(),
  nutrients: nutrientsSchema.partial(),
});
export type FdcFood = z.infer<typeof fdcFoodSchema>;
export const fdcSearchSchema = z.object({ foods: z.array(fdcFoodSchema) });
